import { useFormik } from "formik";
import { ArrowLeft, ArrowRight, Info, Mail, MailCheck, Send } from "lucide-react";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import logo from "@/assets/altLogo.svg";
import image2 from "@/assets/fuundo_ondas.png";
import image1 from "@/assets/fuundo_ondas (1).png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toast";
import api from "@/utils/api";
import AuthLayout from "@/components/Layout/AuthLayout";
import "@/styles/auth-theme.css";

export const RecoveryRequest = () => {
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [retrySeconds, setRetrySeconds] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { type = "Parceiro" } = location.state || {};

  React.useEffect(() => {
    if (retrySeconds === 0) return undefined;
    const timer = setTimeout(() => setRetrySeconds(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [retrySeconds]);

  const form = useFormik({
    initialValues: {
      email: "",
    },

    validationSchema: yup.object({
      email: yup.string().email("Formato de email inválido.").required("Email é obrigatório!"),
    }),

    onSubmit: (values) => handleContinue(values),
  });

  const handleContinue = async (values) => {
    try {
      setLoading(true);
      setErrorMessage("");

      if (type === "Parceiro") {
        const { data } = await api.post("/partners/recover_password", {
          data: {
            attributes: {
              email: values.email,
            },
          },
        });
        if (data) {
          setSent(true);
          setRetrySeconds(45);
        }
      } else {
        const { data } = await api.post("/affiliates/recover_password", {
          data: {
            attributes: {
              email: values.email,
            },
          },
        });
        if (data) {
          toast.success("Email de recuperação enviado com sucesso! Confira sua caixa de entrada!");
          navigate("/vendas");
        }
      }

      setLoading(false);
    } catch (error) {
      setErrorMessage("Não foi possível enviar o e-mail. Confira o endereço e tente novamente.");
      toast.error("Ocorreu um erro ao enviar o email de recuperação.");
    } finally {
      setLoading(false);
    }
  };

  if (type === "Parceiro") {
    return (
      <AuthLayout action={<><span>Lembrou a senha?</span><Link to="/">Entrar</Link></>}>
        {sent ? (
          <>
            <div className="partner-auth-status-icon"><MailCheck /></div>
            <h1>Verifique seu e-mail</h1>
            <p className="partner-auth-description">Enviamos um link de recuperação para {form.values.email}.</p>
            <div className="partner-auth-note"><Info /><span>Não encontrou? Confira a caixa de spam ou promoções.<br />O remetente é noreply@sacgpt.com.br.</span></div>
            <Link to="/" className="partner-auth-submit"><ArrowLeft />Voltar para o login</Link>
            <div className="partner-auth-resend">Não recebeu? <button type="button" disabled={loading || retrySeconds > 0} onClick={() => handleContinue(form.values)}>{loading ? 'Enviando...' : retrySeconds > 0 ? `Reenviar em 0:${String(retrySeconds).padStart(2, '0')}` : 'Reenviar e-mail'}</button></div>
          </>
        ) : (
          <>
            <h1>Recuperar senha</h1>
            <p className="partner-auth-description">Informe o e-mail da sua conta. Enviaremos um link para você criar uma nova senha.</p>
            <form onSubmit={form.handleSubmit} noValidate>
              <div className="partner-auth-field">
                <label htmlFor="email">E-mail</label>
                <div className="partner-auth-input"><Mail aria-hidden="true" /><input id="email" name="email" type="email" autoComplete="email" placeholder="voce@empresa.com.br" value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur} aria-invalid={!!(form.touched.email && form.errors.email)} aria-describedby={form.touched.email && form.errors.email ? 'recovery-email-error' : undefined} /></div>
                {form.touched.email && form.errors.email && <p id="recovery-email-error" className="partner-auth-error">{form.errors.email}</p>}
              </div>
              <button type="submit" className="partner-auth-submit" disabled={loading}><Send />{loading ? 'Enviando...' : 'Enviar link de recuperação'}</button>
            </form>
            <Link to="/" className="partner-auth-link partner-auth-back"><ArrowLeft size={14} />Voltar para o login</Link>
          </>
        )}
        {errorMessage && <p role="alert" className="partner-auth-error">{errorMessage}</p>}
      </AuthLayout>
    );
  }

  return (
    <div className="auth-container">
      {/* Background Images */}
      <img src={image1} className="auth-bg-image auth-bg-image-1" alt="Background decoration" />
      <img src={image2} className="auth-bg-image auth-bg-image-2" alt="Background decoration" />

      {/* Main Content */}
      <div className="auth-content">
        <div className="auth-logo-container">
          <a href="/">
            <img src={logo} alt="SacGPT Logo" className="auth-logo" />
          </a>
        </div>

        <Card className="auth-card">
          <CardHeader className="text-center space-y-4">
            <div className="auth-title">
              <Mail className="w-8 h-8" />
              Recuperação de Conta
            </div>
            <p className="auth-subtitle">Digite seu e-mail para receber o link de recuperação</p>
          </CardHeader>

          <CardContent className="space-y-6">
            <form onSubmit={form.handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="auth-label">
                  E-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={form.values.email}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  className="auth-input"
                  required
                />
                {form.touched.email && form.errors.email && (
                  <div className="auth-error">{form.errors.email}</div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Enviaremos um link de recuperação para este e-mail
                </p>
              </div>

              <Button type="submit" disabled={loading} className="auth-button-primary">
                {loading ? (
                  <>
                    <div className="auth-loading-spinner" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <span>Enviar</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button variant="link" onClick={() => navigate("/")} className="auth-link text-sm">
                Voltar para o login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="auth-footer">sacgpt.com.br © 2025 - Todos os Direitos Reservados</footer>
    </div>
  );
};
